import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Building2, Flame, MessageCircleQuestion, Moon, Search, Sun } from 'lucide-react';
import { useDepartments } from '@/api/queries';
import type { Me } from '@/api/types';
import { CommandPalette, type CommandItem, useDisclosure } from '@/kit';
import { OPEN_FIND_EVENT } from '@/features/apps/FindAnything';
import type { MenuItemDecl } from '@/features/apps/apps-api';
import { useTheme } from '@/hooks/useTheme';
import { useAskLabel } from '@/features/ask';

/**
 * R11 #649: the palette's "Go to" entries are exactly what the shell shows this user — the platform
 * entries, the current app's menus and the Admin pages (`navigableItems`), so ⌘K never offers a page
 * the navigation hides.
 */
export function navCommandItems(items: readonly MenuItemDecl[], navigate: (to: string) => void): CommandItem[] {
  return items.map((n) => ({
    id: `nav:${n.route}`,
    label: n.label,
    group: 'Go to',
    icon: <ArrowRight className="h-4 w-4" aria-hidden />,
    run: () => navigate(n.route),
  }));
}

/** Navigation + department jumps. L7 can extend this with natural-language commands. */
export function AppCommandPalette({ me, nav: navItems, onAsk }: { me?: Me; nav: readonly MenuItemDecl[]; onAsk: () => void }) {
  const navigate = useNavigate();
  const departments = useDepartments(me);
  const { theme, toggleTheme } = useTheme();
  // R-14 T-087: Find anything keeps its tier (`shell.find`; no row → Expert); R-16 v1.2: it is reached from the palette
  const findShown = useDisclosure('shell.find');
  const askLabel = useAskLabel(); // R-16 T-128 (LH): "Ask <platform name>" from meta
  const navKey = navItems.map((n) => n.route).join('|');
  const items = useMemo<CommandItem[]>(() => {
    const nav = navCommandItems(navItems, navigate);

    const depts: CommandItem[] = (departments.data ?? []).map((d) => ({
      id: `dept:${d.key}`,
      label: d.name,
      hint: d.canEdit ? 'You can edit this department' : 'Read-only for you',
      group: 'Departments',
      icon: <Building2 className="h-4 w-4" aria-hidden />,
      run: () => navigate(`/registry?dept=${encodeURIComponent(d.key)}`),
    }));

    const actions: CommandItem[] = [
      ...(findShown
        ? [
            {
              id: 'action:find',
              label: 'Find anything…',
              hint: 'Records across the platform, grouped by the question they answer',
              group: 'Actions',
              icon: <Search className="h-4 w-4" aria-hidden />,
              run: () => window.dispatchEvent(new Event(OPEN_FIND_EVENT)),
            },
          ]
        : []),
      {
        id: 'action:ask',
        label: askLabel,
        hint: 'Answer a question from the capabilities and the library, with citations',
        group: 'Actions',
        icon: <MessageCircleQuestion className="h-4 w-4" aria-hidden />,
        run: onAsk,
      },
      {
        id: 'action:hot-list',
        label: 'Show hot-list capabilities',
        hint: 'Capabilities on the hot list',
        group: 'Actions',
        icon: <Flame className="h-4 w-4" aria-hidden />,
        run: () => navigate('/registry?hot=1'),
      },
      {
        id: 'action:theme',
        label: theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme',
        group: 'Actions',
        icon: theme === 'dark' ? <Sun className="h-4 w-4" aria-hidden /> : <Moon className="h-4 w-4" aria-hidden />,
        run: toggleTheme,
      },
    ];

    return [...nav, ...depts, ...actions];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- navKey stands for the navigable entries
  }, [departments.data, navigate, onAsk, theme, toggleTheme, navKey, findShown, askLabel]);

  return <CommandPalette items={items} />;
}
